import { Opportunity } from "@/opportunity/opportunity";
import React, { useState } from "react";
import { useGenerateShareableLink } from "@/opportunity/api";
import OpportunityDescriptionEmail from "@/opportunity/OpportunityDescriptionEmail";
import { render } from "@react-email/render";
import { toast } from 'sonner';

export const useGenerateAndCopyOpportunityEmail = (opportunity: Opportunity) => {
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [generatedEmailHtml, setGeneratedEmailContentHtml] = useState<string>();
  const [isGeneratedEmailHtmlCopied, setIsGeneratedEmailHtmlCopied] = React.useState(false);

  const { generateShareableLink } = useGenerateShareableLink(opportunity.id);

  const generateEmailContent = async () => {
    setIsGeneratingEmail(true);
    try {
      const link = await generateShareableLink();
      const htmlContent = await render(<OpportunityDescriptionEmail opportunity={opportunity} shareableLink={link}/>);
      setGeneratedEmailContentHtml(htmlContent);
    } catch (error) {
      toast.error("Error generating email content");
      console.error('Error generating email content:', error);
    } finally {
      setIsGeneratingEmail(false);
    }
  }

  const copyEmailContent = async () => {
    if (generatedEmailHtml) {
      try {
        if (navigator.clipboard && navigator.clipboard.write) {
          const type = "text/html";
          const blob = new Blob([generatedEmailHtml], { type });
          const data = [new ClipboardItem({ [type]: blob })];
          await navigator.clipboard.write(data);
        } else {
          copyHtmlUsingContentEditable(generatedEmailHtml);
        }
        setIsGeneratedEmailHtmlCopied(true);
        console.log("Email content copied to clipboard!");
      } catch (error) {
        console.error("Failed to copy email content:", error);
        setIsGeneratedEmailHtmlCopied(false);
        alert("Failed to copy email content. Please try manually.");
      }
    }
  };

  const emailSubject = `Thalo Copilot Update - ${opportunity.name}, ${opportunity.location.name}`
  const [isEmailSubjectCopied, setIsEmailSubjectCopied] = useState(false);

  const copyEmailSubject = async () => {
    try {
      await navigator.clipboard.writeText(emailSubject);
      setIsEmailSubjectCopied(true);
    } catch (error) {
      console.error("Failed to copy email subject:", error);
      setIsEmailSubjectCopied(false);
      alert("Failed to copy email subject. Please try manually.");
    }
  }

  const copyHtmlUsingContentEditable = (html: string) => {
    const tempElement = document.createElement("div");
    tempElement.contentEditable = "true";
    tempElement.style.position = "absolute";
    tempElement.style.left = "-9999px";
    tempElement.innerHTML = html;
    document.body.appendChild(tempElement);

    const range = document.createRange();
    range.selectNodeContents(tempElement);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);

    try {
      const successful = document.execCommand("copy");
      if (!successful) {
        throw new Error("Copy command was unsuccessful");
      }
    } catch (err) {
      console.error("Fallback: Oops, unable to copy", err);
      alert("Failed to copy email content. Please try manually.");
    }

    document.body.removeChild(tempElement);
    selection?.removeAllRanges();
  };

  return {
    generateEmailContent,
    isGeneratingEmail,
    generatedEmailHtml,
    isGeneratedEmailHtmlCopied,
    setIsGeneratedEmailHtmlCopied,
    copyEmailContent,
    copyEmailSubject,
    isEmailSubjectCopied,
    emailSubject,
    setIsEmailSubjectCopied,
  }
}